import {
  EDatasourceType,
  EValueType,
  IActionDefinitionDto,
  IActionParameter,
  IDatabaseColumnModel,
  IDatabaseTableModel,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
import Editor, { OnMount } from "@monaco-editor/react";
import Form, { FormInstance } from "antd/es/form";
import FormItem from "antd/es/form/FormItem";
import * as monaco from "monaco-editor";
import type { IDisposable } from "monaco-editor";
import ObjectHash from "object-hash";
import type { CollapseProps } from "rc-collapse";
import * as React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { DatasourceService } from "../../../../services/datasource-service";
import { LanguageServerService } from "../../../../services/language-server-service";
import SqlService from "../../../../services/sql-service";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { getDataTypeIconName } from "../../../../shared/utility/data-type-icon";
import { resolveLoadingPresentation } from "../../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import {
  attachSqlEditor,
  SqlLspContext,
  SqlSchemaTable,
  toLspLanguageId,
  updateSqlDiagnostics
} from "../../../../shared/utility/sql-language-features";
import TableSvg from "../../../../svgs/table.svg";
import { ActionUsagesButton } from "../../../common/action-usages-button";
import { ActionUsedInProcessAlert } from "../../../common/action-used-in-process-alert";
import {
  AICodeGenerator,
  EAICodeGeneratorType,
  IAICodeGeneratorSuggestion
} from "../../../common/ai/ai-code-generator";
import { IPlannedCodeInsertion } from "../../../common/ai/ai-code-insertion";
import { AiPendingEdit } from "../../../common/ai/ai-pending-edit";
import { AiPendingEditBar } from "../../../common/ai/ai-pending-edit-bar";
import CloseConfirmationModal from "../../../common/close-confirmation-modal";
import { SQLActionParameters } from "../../../common/ds/sql-action-parameters";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import { copyTextToClipboard } from "../../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSpinnerSmall from "../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { CodeEditorDrawer, ECodeEditorDrawerTab } from "../../code-editor-shell/code-editor-drawer";
import { CodeEditorHeader } from "../../code-editor-shell/code-editor-header";
import { CodeEditorProblemList } from "../../code-editor-shell/code-editor-problem-list";
import { readRunErrorMessage } from "../../code-editor-shell/code-editor-problems";
import { ICodeEditorProblem, readModelProblems } from "../../code-editor-shell/code-editor-problems";
import { CodeEditorResultTable } from "../../code-editor-shell/code-editor-result-table";
import { CodeEditorSidePanel, ECodeEditorSideTab } from "../../code-editor-shell/code-editor-side-panel";
import { CodeEditorSplitter } from "../../code-editor-shell/code-editor-splitter";
import {
  CODE_EDITOR_THEME,
  defineCodeEditorTheme,
  watchCodeEditorTheme
} from "../../code-editor-shell/code-editor-theme";
import { CodeEditorToolbar, ICodeEditorCursor } from "../../code-editor-shell/code-editor-toolbar";
import { requestDatasourceReveal } from "../../data-diagram/datasource-reveal";
import { SchemaNode } from "./datasources";

/** The three example prompts KOA-13392 offers in the empty Kuika AI panel. */
const SQL_ACTION_AI_SUGGESTIONS: IAICodeGeneratorSuggestion[] = [
  { prompt: "Write a SQL query to fetch all records from this table", icon: "database-cog" },
  { prompt: "Filter results created in the last 30 days", icon: "filter-cog" },
  { prompt: "Select only active records", icon: "report" }
];

export interface ISqlActionModalState {
  code: string;
  actionParameters: IActionParameter[];
  highlightedText: string | undefined;
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  resultList: any[];
  resultListColumns: any[];
  isResultListOpen: boolean;
  commentedParams?: string[];
  shouldGenerateWithAI: boolean;
  isSidePanelOpened: boolean;
  errorMessage?: string;
  selectedPunto: number;
  wrapStatus: string;
  showUnsavedChangesModal: boolean;
  isParametersLoading: boolean;
  /** Parameter names a type lookup has already come back for — see SQLActionParameters. */
  checkedTypeNames: string[];
  typingTimeout?: any;
  schemaAreaWidth: number;
  schemaSearch: string;
  parametersPanelWidth: number;
  isTestButtonLoading: boolean;
  formatCompleted: boolean;
  /** The AI edit waiting for the user's confirmation; the editor still holds their own query. */
  pendingAiEdit: IPlannedCodeInsertion | null;
  drawerTab: ECodeEditorDrawerTab;
  sideTab: ECodeEditorSideTab;
  cursor: ICodeEditorCursor | null;
  problems: ICodeEditorProblem[];
  /** What the database refused the last run with; cleared by the next one. */
  runError: string | null;
  lastRunDurationMs: number | null;
  /** Dragged by the grips, so both panels keep the size the user gave them. */
  drawerHeight: number;
  /** Folded from the drawer's own grip; the editor takes the room. */
  isResultDrawerOpen: boolean;
  /** Set while a table is over the code surface, and the menu its drop opened. */
  isDraggingTable: boolean;
  dropMenu: ISqlActionDropMenu | null;
  dropMenuActiveIndex: number;
}

export interface ISqlActionModalProps {
  closeModal: any;
  datasource: IDatasourceDto;
  actionToEdit: IActionDefinitionDto;
  schema: SchemaNode[];
  refreshAction: boolean;
  sd: IScreenDesignContextState;
  folderToAdd: string | undefined;
}

type AllProps = ISqlActionModalProps & IWithHistoryProp;

/** One entry of the menu a dropped table offers, and where that menu was dropped. */
export interface ISqlActionTableScriptOption {
  key: string;
  label: string;
  onClick: () => void;
}

export interface ISqlActionDropMenu {
  item: SchemaNode;
  x: number;
  y: number;
}

/** What the editor leaves of the screen on each side. */
const MODAL_MARGIN: number = 40;

class SqlActionModal extends React.PureComponent<AllProps, ISqlActionModalState> {
  formRef = React.createRef<FormInstance>();

  testResultsRef = React.createRef<HTMLDivElement>();

  editorRef: Parameters<OnMount>[0] | null = null;

  lastClickedRow: number | undefined;

  sqlLspDisposable: IDisposable | undefined;

  markerDisposable: IDisposable | undefined;

  sqlMonaco: typeof monaco | undefined;
  codeThemeObserver: MutationObserver | undefined;

  // dataTransfer can only carry text, so the node itself is held here.
  draggedSchemaNode: SchemaNode | null = null;

  // setDragImage needs a rendered node, so the ghost stays in the DOM, off-screen.
  dragGhostRef = React.createRef<HTMLDivElement>();

  dragGhostLabelRef = React.createRef<HTMLSpanElement>();

  dropMenuRef = React.createRef<HTMLDivElement>();

  aiPendingEdit: AiPendingEdit = new AiPendingEdit({
    onChange: (pendingAiEdit: IPlannedCodeInsertion | null) => this.setState({ pendingAiEdit })
  });

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: AllProps) {
    super(props);

    this.state = {
      code: "",
      actionParameters: [],
      highlightedText: undefined,
      saveStatus: "idle",
      resultList: [],
      resultListColumns: [],
      isResultListOpen: false,
      commentedParams: [],
      shouldGenerateWithAI: false,
      isSidePanelOpened: true,
      selectedPunto: 14,
      wrapStatus: "on",
      showUnsavedChangesModal: false,
      isParametersLoading: false,
      checkedTypeNames: [],
      typingTimeout: undefined,
      schemaAreaWidth: 223,
      schemaSearch: "",
      parametersPanelWidth: 288,
      isTestButtonLoading: false,
      formatCompleted: false,
      pendingAiEdit: null,
      drawerTab: "result",
      sideTab: "coach",
      cursor: null,
      problems: [],
      runError: null,
      lastRunDurationMs: null,
      drawerHeight: 258,
      isResultDrawerOpen: false,
      isDraggingTable: false,
      dropMenu: null,
      dropMenuActiveIndex: 0
    };
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.sqlLspDisposable?.dispose();
    this.markerDisposable?.dispose();
    this.codeThemeObserver?.disconnect();
    this.aiPendingEdit.dispose();
  }

  componentDidMount() {
    const actionParameters = this.props?.actionToEdit?.actionParameters || [];
    const code = this.props?.actionToEdit?.statement;

    // Loaded from the saved action, so their types are whatever the last lookup settled on: one
    // still Unknown here is genuinely unresolved and says so straight away.
    const checkedTypeNames: string[] = actionParameters.map((parameter: IActionParameter): string => parameter.name);
    this.setState({ actionParameters, checkedTypeNames, code }, () => {
      this.objectHash = this.getObjectHash();
    });

    this.props.sd?.datasources?.map((datasource) => {
      if (datasource.id != "") {
        this.props.sd?.getTables(datasource?.id);
      }
    });

    if (this.props.refreshAction) {
      this.props.closeModal(true);
      this.saveAction();
    }

    setTimeout(() => {
      this.handleGenerateParameters();
    }, 2000);

    // MCP auto-save: when triggered from MCP tool, auto-submit after parameters are detected
    if ((window as any).__mcpAutoSaveSqlAction) {
      delete (window as any).__mcpAutoSaveSqlAction;
      setTimeout(() => {
        this.formRef.current?.submit();
      }, 3000);
    }
  }

  componentDidUpdate(prevProps: Readonly<AllProps>, prevState: Readonly<ISqlActionModalState>) {
    // Generic select logic moved to datasources.tsx
  }

  checkAndHandleGenerateParameterTypes = () => {
    const currentParams = JSON.stringify(this.state.actionParameters);
    if (currentParams.includes("Unknown")) {
      this.handleGenerateParameters();
    }
  };

  getObjectHash = () => {
    const { ActionName, Statement } = this.formRef.current?.getFieldsValue() || {};
    const { actionParameters } = { ...this.state };

    return ObjectHash({ ActionName, Statement, actionParameters });
  };

  handleCloseModal = () => {
    if (this.objectHash === this.getObjectHash()) {
      this.props.closeModal();
      return;
    }

    this.setState({ showUnsavedChangesModal: true });
  };

  handleEditorMount: OnMount = (editor) => {
    this.editorRef = editor;
    if (this.sqlMonaco) {
      this.sqlLspDisposable = attachSqlEditor(this.sqlMonaco, editor.getModel(), this.getSqlLspContext());
      this.aiPendingEdit.attach(this.sqlMonaco, editor);
    }
    editor.onDidChangeCursorSelection(this.getHighlightedText);
    editor.onDidChangeCursorPosition((event) => this.setState({ cursor: event.position }));
    editor.updateOptions({ fontSize: this.state.selectedPunto });
    this.markerDisposable = this.sqlMonaco?.editor.onDidChangeMarkers(() => this.refreshProblems());
    editor.onMouseUp((event) => {
      const position = event.target.position;
      this.lastClickedRow = position?.lineNumber;
    });
  };

  handleWrap = (wrapType: string) => {
    const editor = this.editorRef;

    if (editor) {
      editor.updateOptions({ wordWrap: wrapType as "on" | "off" });
      this.setState({ wrapStatus: wrapType });
    }
  };

  handlePasteToLastClickedRow = () => {
    const editor = this.editorRef;
    const lastClickedRow = this.lastClickedRow;

    if (editor && lastClickedRow) {
      navigator.clipboard
        .readText()
        .then((text) => {
          // @ts-ignore
          editor?.trigger("keyboard", "type", { text });
        })
        .catch((error) => {
          KMainFunctions.exceptionHandler(error);
        });
    }
  };

  editorBeforeMount = (monacoInstance: typeof monaco) => {
    this.sqlMonaco = monacoInstance;
    defineCodeEditorTheme(monacoInstance);
    this.codeThemeObserver = watchCodeEditorTheme(monacoInstance);
  };

  getSqlLspContext = (): SqlLspContext => ({
    getDatasourceId: () => this.props.datasource?.id ?? null,
    getLanguageId: () => toLspLanguageId(this.props.datasource?.datasourceType),
    getDatasourceType: () => this.props.datasource?.datasourceType,
    getSchema: () => this.getClientSchemaTables(),
    ensureColumns: (tableNames: string[]) => this.ensureSchemaColumnsLoaded(tableNames)
  });

  /** Normalizes the active datasource's loaded schema into the shape the SQL completion provider expects. */
  getClientSchemaTables = (): SqlSchemaTable[] => {
    const datasourceId: string | undefined = this.props.datasource?.id;
    if (!datasourceId) return [];
    const tables: IDatabaseTableModel[] = this.props.sd?.schemas?.get(datasourceId)?.tables ?? [];
    return tables
      .filter((table: IDatabaseTableModel): boolean => !table.dropped)
      .map((table: IDatabaseTableModel): SqlSchemaTable => ({
        name: table.title || table.name,
        schemaName: table.schema,
        columns: (table.columns ?? [])
          .filter((column: IDatabaseColumnModel): boolean => !column.dropped)
          .map((column: IDatabaseColumnModel): string => column.name)
      }));
  };

  /** Hydrates columns for referenced tables (KOA-17026 slim load) so column autocomplete works. */
  ensureSchemaColumnsLoaded = async (tableNames: string[]): Promise<void> => {
    const datasourceId: string | undefined = this.props.datasource?.id;
    if (!datasourceId || !this.props.sd?.ensureTableColumnsLoaded) return;
    await this.props.sd.ensureTableColumnsLoaded(datasourceId, tableNames);
  };

  handleFormatSql = async () => {
    const editor = this.editorRef;
    const model = editor?.getModel();
    if (!editor || !model) return;

    const sourceCode = model.getValue();
    const languageId = toLspLanguageId(this.props.datasource?.datasourceType);
    const formattedCode = await LanguageServerService.Format(sourceCode, languageId);
    if (!formattedCode) return;

    editor.executeEdits("format", [{ range: model.getFullModelRange(), text: formattedCode }]);
    this.formRef.current?.setFieldsValue({ Statement: formattedCode });
    this.setState({ code: formattedCode, formatCompleted: true });
    updateSqlDiagnostics(this.sqlMonaco, model, formattedCode, this.getSqlLspContext());
    setTimeout(() => this.setState({ formatCompleted: false }), 3000);
  };

  /** Writes the AI edit the user just confirmed and re-runs what typing would have triggered. */
  handleApplyPendingAiEdit = () => {
    const newValue = this.aiPendingEdit.commit();
    if (newValue === null) return;

    this.formRef.current?.setFieldsValue({ Statement: newValue });
    this.setState({ code: newValue });
    updateSqlDiagnostics(this.sqlMonaco, this.editorRef?.getModel(), newValue, this.getSqlLspContext());
  };

  getHighlightedText = () => {
    const selection = this.editorRef?.getSelection();
    const highlightedText =
      selection && !selection.isEmpty() ? (this.editorRef?.getModel()?.getValueInRange(selection) as string) : "";
    this.setState({ highlightedText });
  };

  onChange = (newValue: string | undefined, e) => {
    clearTimeout(this.state.typingTimeout);

    updateSqlDiagnostics(this.sqlMonaco, this.editorRef?.getModel(), newValue, this.getSqlLspContext());

    const newTimeout = setTimeout(() => {
      this.checkAndHandleGenerateParameterTypes();
    }, 2000);

    if (!newValue) {
      this.setState({ highlightedText: "" });
      return;
    }

    const paramsFromCode = SqlService.getSqlParameters(this.props.datasource?.datasourceType, newValue);

    const newParameters = [] as IActionParameter[];
    const currentActionParameters = [...this.state.actionParameters];

    paramsFromCode.forEach((paramFromCode) => {
      paramFromCode = paramFromCode.replace(/^@/, "").replace(/^:/, "");
      const curData = currentActionParameters.find((x) => x.name === paramFromCode);
      const paramObj = {} as IActionParameter;
      paramObj.name = paramFromCode;
      paramObj.Typename = curData ? curData.Typename : "Unknown";
      newParameters.push(paramObj);
    });

    if (!newValue) {
      this.setState({ highlightedText: "" });
    }

    this.setState({
      actionParameters: newParameters,
      code: newValue,
      typingTimeout: newTimeout
    });
  };

  getTableScriptOptions = (item: SchemaNode): ISqlActionTableScriptOption[] => [
    { key: "select", label: "Add Select Query", onClick: () => this.generateSelectScript(item) },
    { key: "select-by-id", label: "Add Select By Id Query", onClick: () => this.generateSelectByIdScript(item) },
    { key: "save", label: "Add Save Query", onClick: () => this.generateSaveScript(item) },
    { key: "delete", label: "Add Delete Query", onClick: () => this.generateDeleteScript(item) },
    { key: "search", label: "Add Search By Keyword Query", onClick: () => this.generateSearchByKeywordScript(item) },
    {
      key: "search-paginated",
      label: "Add Search With Pagination Query",
      onClick: () => this.generateSearchWithPaginationScript(item)
    }
  ];

  /**
   * One panel per table: the name and the row's two controls in the header, the columns in
   * the body. `KuikaCollapsePanel` brings the row chrome — 36px header, hover, divider,
   * chevron — so nothing here restates it.
   */
  getSchemaItems = (): CollapseProps["items"] =>
    (this.props?.schema ?? [])
      .filter((item: SchemaNode): boolean =>
        this.state.schemaSearch ? item.title.toLowerCase().includes(this.state.schemaSearch.toLowerCase()) : true
      )
      .map((item: SchemaNode, index: number) => ({
        key: String(index),
        className: "kuika__ds-collapse__item",
        label: (
          <span
            className="kuika__ds-collapse__label"
            draggable
            onDragStart={this.handleSchemaDragStart(item)}
            onDragEnd={this.handleSchemaDragEnd}
            data-testid="sqlActionSchemaTable"
          >
            <TableSvg />
            {/* The column is narrow enough to clip a long name, so the whole one is on hover —
                the app's tooltip, not the browser's, which takes a second to appear. */}
            <KuikaTooltip content={<div>{item.title}</div>} position="right">
              <span className="kuika__ds-collapse__label-text">{item.title}</span>
            </KuikaTooltip>
          </span>
        ),
        extra:
          item.isLeaf === true ? undefined : (
            <div className="kuika__ds-collapse__row-actions" onClick={(e) => e.stopPropagation()}>
              <KuikaTooltip content={<div>Drag onto the editor to insert a query</div>}>
                <span
                  className="kuika__ds-collapse__drag-handle"
                  draggable
                  onDragStart={this.handleSchemaDragStart(item)}
                  onDragEnd={this.handleSchemaDragEnd}
                  data-testid="sqlActionSchemaTableDragHandle"
                >
                  {/* A line glyph, masked: the two-tone badge carried its own light background
                      and stayed pale on the dark theme. */}
                  <KuikaSvg name="drag-drop" width={16} height={16} />
                </span>
              </KuikaTooltip>
              <KuikaDropdownMenu placement="bottomRight" options={this.getRowMenuOptions(item)}>
                <KuikaButton
                  type="tertiary"
                  color="neutral"
                  isIcon
                  size="sm"
                  aria-label={`Queries for ${item.title}`}
                  data-testid="sqlActionSchemaTableMenu"
                >
                  <KuikaSvg type="styling-panel-icons" name="dots-vertical" width={16} height={16} />
                </KuikaButton>
              </KuikaDropdownMenu>
            </div>
          ),
        children: (
          <div className="kuika__ds-collapse__columns">
            {item.children.map((col: SchemaNode, columnIndex: number) => {
              // Same glyph table the Parameters panel reads, so a column is the same type on
              // both sides of the editor.
              const typeIconName: string | undefined = getDataTypeIconName(col.kuikaDataType);

              return (
                <div className="kuika__ds-collapse__label child-label" key={columnIndex}>
                  {typeIconName ? (
                    <KuikaSvg
                      type="action-icons"
                      name={typeIconName}
                      width={16}
                      height={16}
                      className="kuika__ds-collapse__type-icon"
                    />
                  ) : (
                    // The slot stays: an unmapped type must not shunt its column name out of
                    // line with the rest.
                    <span className="kuika__ds-collapse__type-icon" />
                  )}
                  <KuikaTooltip
                    content={<div>{col.kuikaDataType ? `${col.title} — ${col.kuikaDataType}` : col.title}</div>}
                    position="right"
                  >
                    <span className="kuika__ds-collapse__label-text">{col.title}</span>
                  </KuikaTooltip>
                </div>
              );
            })}
          </div>
        )
      }));

  /** The row menu's options in the ui-kit dropdown's own shape. */
  getRowMenuOptions = (owner: SchemaNode): KuikaDropdownMenuItemType[] =>
    this.getTableScriptOptions(owner).map((option: ISqlActionTableScriptOption) => ({
      key: option.key,
      label: option.label,
      onClick: option.onClick
    }));

  // Dragging a table onto the code offers the same six scripts the row menu does, at the
  // point of the drop.
  handleSchemaDragStart =
    (item: SchemaNode) =>
    (event: React.DragEvent<HTMLElement>): void => {
      this.draggedSchemaNode = item;
      event.dataTransfer.effectAllowed = "copy";
      event.dataTransfer.setData("text/plain", item.title);
      const ghost: HTMLDivElement | null = this.dragGhostRef.current;
      const ghostLabel: HTMLSpanElement | null = this.dragGhostLabelRef.current;
      if (ghost && ghostLabel) {
        ghostLabel.textContent = item.title;
        event.dataTransfer.setDragImage(ghost, 16, 16);
      }
    };

  handleSchemaDragEnd = (): void => {
    this.draggedSchemaNode = null;
    if (this.state.isDraggingTable) this.setState({ isDraggingTable: false });
  };

  handleEditorDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    if (!this.draggedSchemaNode) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    if (!this.state.isDraggingTable) this.setState({ isDraggingTable: true });
  };

  handleEditorDragLeave = (): void => {
    if (this.state.isDraggingTable) this.setState({ isDraggingTable: false });
  };

  handleEditorDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    const item: SchemaNode | null = this.draggedSchemaNode;
    this.draggedSchemaNode = null;
    if (!item) return;
    event.preventDefault();
    const bounds: DOMRect = event.currentTarget.getBoundingClientRect();
    this.setState(
      {
        isDraggingTable: false,
        dropMenu: { item, x: event.clientX - bounds.left, y: event.clientY - bounds.top },
        dropMenuActiveIndex: 0
      },
      () => {
        this.dropMenuRef.current?.focus();
      }
    );
  };

  handleDropMenuSelect = (option: ISqlActionTableScriptOption): void => {
    option.onClick();
    this.setState({ dropMenu: null, dropMenuActiveIndex: 0 });
  };

  handleDropMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const item: SchemaNode | undefined = this.state.dropMenu?.item;
    if (!item) return;
    const options: ISqlActionTableScriptOption[] = this.getTableScriptOptions(item);
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (options.length === 0) return;
      const delta: number = event.key === "ArrowDown" ? 1 : -1;
      const next: number = (this.state.dropMenuActiveIndex + delta + options.length) % options.length;
      this.setState({ dropMenuActiveIndex: next });
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const option: ISqlActionTableScriptOption | undefined = options[this.state.dropMenuActiveIndex];
      if (option) this.handleDropMenuSelect(option);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      this.setState({ dropMenu: null, dropMenuActiveIndex: 0 });
    }
  };

  getActionScript = (item, scriptType) => {
    let newScript: string;
    newScript = "";
    const datasource = this.props.sd?.schemas.get(this.props.datasource?.id ?? "");
    const table = datasource?.tables.filter((tables) => tables.title === item.title)[0];
    const datasourceType = this.props.datasource.datasourceType;
    if (
      datasourceType == EDatasourceType.ManagedDb ||
      datasourceType == EDatasourceType.MsSql ||
      datasourceType == EDatasourceType.CustomDb
    ) {
      if (table && scriptType) {
        if (scriptType === "select") {
          newScript = `SELECT * FROM ${table.schema}.${table.title}`;
        } else if (scriptType === "selectById") {
          newScript = `SELECT * FROM ${table.schema}.${table.title} WHERE Id = @Id`;
        } else if (scriptType === "update") {
          let fields = table.columns.filter((x) => x.name !== "Id");
          fields = fields.filter((x) => x.isIdentity !== true);
          const fieldAssignments = fields.map((x) => `${x.name} = @${x.name}`).join(", ");
          const fieldNames = fields.map((x) => `${x.name}`).join(", ");
          const values = fields.map((x) => `@${x.name}`).join(", ");
          const sql = `UPDATE ${table.schema}.${table.title}
    SET ${fieldAssignments} WHERE Id = @Id
    IF @@ROWCOUNT = 0
    BEGIN
      INSERT INTO ${table.schema}.${table.title} (Id, ${fieldNames})
      VALUES (@Id, ${values});
      END`;

          newScript = sql;
        } else if (scriptType === "delete") {
          newScript = `DELETE FROM ${table.schema}.${table.title} WHERE Id = @Id`;
        } else if (scriptType === "searchByKeyword") {
          let fields = table.columns.filter((x) => x.sqlDataType === "nvarchar");

          if (!fields.length) {
            KMainFunctions.displayErrorNotification(
              "In order to add this SQL, you should choose a Table with at least 1 string column",
              { title: "No String column found" }
            );
            return;
          }

          const conditions = fields.map((x) => `${x.name} LIKE @keyword`).join(" OR\n");

          newScript = `SELECT * FROM ${table.schema}.${table.title} \nWHERE ${conditions} OR\n@keyword IS NULL \nORDER BY ${fields[0].name}`;
        } else if (scriptType === "searchWithPagination") {
          let fields = table.columns.filter((x) => x.sqlDataType === "nvarchar");

          if (!fields.length) {
            KMainFunctions.displayErrorNotification(
              "In order to add this SQL, you should choose a Table with at least 1 string column",
              { title: "No String column found" }
            );
            return;
          }

          const conditions = fields.map((x) => `${x.name} LIKE @${x.name}`).join(" OR\n");

          newScript = `SELECT * FROM ${table.schema}.${table.title} \nWHERE ${conditions} \nORDER BY ${fields[0].name} \nOFFSET (@currentpage-1)*@pagesize \nROWS FETCH NEXT @pagesize \nROWS ONLY`;
        } else if (scriptType === "empty") {
          newScript = "";
        }
      }
    } else if (datasourceType == EDatasourceType.ManagedDbPostgre || datasourceType == EDatasourceType.Postgre) {
      if (table && scriptType) {
        if (scriptType === "select") {
          newScript = `SELECT * FROM "${table.schema}"."${table.title}"`;
        } else if (scriptType === "selectById") {
          newScript = `SELECT * FROM "${table.schema}"."${table.title}" WHERE "Id" = :Id`;
        } else if (scriptType === "update") {
          let fields = table.columns.filter((x) => x.name !== "Id");
          fields = fields.filter((x) => x.isIdentity !== true);
          const fieldAssignments = fields.map((x) => `"${x.name}" = :${x.name}`).join(", ");
          const fieldNames = fields.map((x) => `"${x.name}"`).join(", ");
          const values = fields.map((x) => `:${x.name}`).join(", ");
          const sql = `
          UPDATE "${table.schema}"."${table.title}"
          SET ${fieldAssignments}
          WHERE "Id" = :Id;

          -- Ekleme işlemi
          INSERT INTO "${table.schema}"."${table.title}" ("Id", ${fieldNames})
          SELECT :Id, ${values}
          WHERE NOT EXISTS (
              SELECT 1
              FROM "${table.schema}"."${table.title}"
              WHERE "Id" = :Id
          );
          `;
          newScript = sql;
        } else if (scriptType === "delete") {
          newScript = `DELETE FROM "${table.schema}"."${table.title}" WHERE "Id" = :Id`;
        } else if (scriptType === "searchByKeyword") {
          let fields = table.columns.filter(
            (x) => x.sqlDataType == "VARCHAR" || x.sqlDataType == "character varying" || x.sqlDataType == "VARCHAR"
          );

          if (!fields.length) {
            KMainFunctions.displayErrorNotification(
              "In order to add this SQL, you should choose a Table with at least 1 string column",
              { title: "No String column found" }
            );
            return;
          }

          const conditions = fields.map((x) => `"${x.name}" LIKE :keyword`).join(" OR\n");

          newScript = `SELECT * FROM "${table.schema}"."${table.title}" \nWHERE ${conditions} OR\n:keyword IS NULL \nORDER BY "${fields[0].name}"`;
        } else if (scriptType === "searchWithPagination") {
          let fields = table.columns.filter(
            (x) => x.sqlDataType == "NVARCHAR" || x.sqlDataType == "character varying" || x.sqlDataType == "VARCHAR"
          );

          if (!fields.length) {
            KMainFunctions.displayErrorNotification(
              "In order to add this SQL, you should choose a Table with at least 1 string column",
              { title: "No String column found" }
            );
            return;
          }

          const conditions = fields.map((x) => `"${x.name}" LIKE :${x.name}`).join(" OR\n");

          newScript = `SELECT * FROM "${table.schema}"."${table.title}" \nWHERE ${conditions} \nORDER BY "${fields[0].name}" \nOFFSET (:currentpage-1)*:pagesize \n LIMIT :pagesize \n`;
        } else if (scriptType === "empty") {
          newScript = "";
        }
      }
    } else if (datasourceType == EDatasourceType.ManagedDbOracle || datasourceType == EDatasourceType.Oracle) {
      if (table && scriptType) {
        if (scriptType === "select") {
          newScript = `SELECT * FROM "${table.title}"`;
        } else if (scriptType === "selectById") {
          newScript = `SELECT * FROM "${table.title}" WHERE "Id" = :Id`;
        } else if (scriptType === "update") {
          let fields = table.columns.filter((x) => x.name !== "Id");
          fields = fields.filter((x) => x.isIdentity !== true);
          const fieldAssignments = fields.map((x) => `"${x.name}" = :${x.name}`).join(", ");
          const fieldNames = fields.map((x) => `"${x.name}"`).join(", ");
          const values = fields.map((x) => `:${x.name}`).join(", ");
          const sql = `
    declare
          v_rowcount integer;
          begin
              UPDATE "${table.title}"
              SET ${fieldAssignments} WHERE "Id" = :Id;
              v_rowcount := SQL%ROWCOUNT;
              IF v_rowcount = 0
              THEN
                INSERT INTO "${table.title}" ("Id", ${fieldNames})
                VALUES (:Id, ${values});
                END IF;
          end;`;
          newScript = sql;
        } else if (scriptType === "delete") {
          newScript = `DELETE FROM "${table.title}" WHERE "Id" = :Id`;
        } else if (scriptType === "searchByKeyword") {
          let fields = table.columns.filter(
            (x) => x.sqlDataType == "NVARCHAR" || x.sqlDataType == "NVARCHAR2" || x.sqlDataType == "VARCHAR"
          );

          if (!fields.length) {
            KMainFunctions.displayErrorNotification(
              "In order to add this SQL, you should choose a Table with at least 1 string column",
              { title: "No String column found" }
            );
            return;
          }

          const conditions = fields.map((x) => `"${x.name}" LIKE :keyword`).join(" OR\n");

          newScript = `SELECT * FROM "${table.title}" \nWHERE ${conditions} OR\n:keyword IS NULL \nORDER BY "${fields[0].name}"`;
        } else if (scriptType === "searchWithPagination") {
          let fields = table.columns.filter(
            (x) => x.sqlDataType == "NVARCHAR" || x.sqlDataType == "NVARCHAR2" || x.sqlDataType == "VARCHAR"
          );

          if (!fields.length) {
            KMainFunctions.displayErrorNotification(
              "In order to add this SQL, you should choose a Table with at least 1 string column",
              { title: "No String column found" }
            );
            return;
          }

          const conditions = fields.map((x) => `"${x.name}" LIKE :${x.name}`).join(" OR\n");

          newScript = `SELECT * FROM "${table.title}" \nWHERE ${conditions} \nORDER BY "${fields[0].name}" \nOFFSET (:currentpage-1)*:pagesize \nROWS FETCH NEXT :pagesize \nROWS ONLY`;
        } else if (scriptType === "empty") {
          newScript = "";
        }
      }
    } else if (table && scriptType) {
      if (scriptType === "select") {
        newScript = `SELECT * FROM ${table.title}`;
      } else if (scriptType === "selectById") {
        newScript = `SELECT * FROM ${table.title} WHERE Id = @Id`;
      } else if (scriptType === "update") {
        let fields = table.columns.filter((x) => x.name !== "Id");
        fields = fields.filter((x) => x.isIdentity !== true);
        const fieldAssignments = fields.map((x) => `${x.name} = @${x.name}`).join(", ");
        const fieldNames = fields.map((x) => `${x.name}`).join(", ");
        const values = fields.map((x) => `@${x.name}`).join(", ");
        const sql = `UPDATE ${table.title}
    SET ${fieldAssignments} WHERE Id = @Id
    IF @@ROWCOUNT = 0
    BEGIN
      INSERT INTO ${table.title} (Id, ${fieldNames})
      VALUES (@Id, ${values});
      END`;

        newScript = sql;
      } else if (scriptType === "delete") {
        newScript = `DELETE FROM ${table.title} WHERE Id = @Id`;
      } else if (scriptType === "searchByKeyword") {
        let fields = table.columns.filter((x) => x.sqlDataType === "nvarchar");

        if (!fields.length) {
          KMainFunctions.displayErrorNotification(
            "In order to add this SQL, you should choose a Table with at least 1 string column",
            { title: "No String column found" }
          );
          return;
        }

        const conditions = fields.map((x) => `${x.name} LIKE @keyword`).join(" OR\n");

        newScript = `SELECT * FROM ${table.title} \nWHERE ${conditions} OR\n@keyword IS NULL \nORDER BY ${fields[0].name}`;
      } else if (scriptType === "searchWithPagination") {
        let fields = table.columns.filter((x) => x.sqlDataType === "nvarchar");

        if (!fields.length) {
          KMainFunctions.displayErrorNotification(
            "In order to add this SQL, you should choose a Table with at least 1 string column",
            { title: "No String column found" }
          );
          return;
        }

        const conditions = fields.map((x) => `${x.name} LIKE @${x.name}`).join(" OR\n");

        newScript = `SELECT * FROM ${table.title} \nWHERE ${conditions} \nORDER BY ${fields[0].name} \nOFFSET (@currentpage-1)*@pagesize \nROWS FETCH NEXT @pagesize \nROWS ONLY`;
      } else if (scriptType === "empty") {
        newScript = "";
      }
    }

    return newScript;
  };

  generateSelectScript(item) {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    fieldsValue.Statement = this.getActionScript(item, "select");
    if (!this.props.actionToEdit || !this.props.actionToEdit?.name) {
      fieldsValue.ActionName = `${item.title}All`;
    }
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.setState({ code: fieldsValue.Statement }, () => {
      this.checkAndHandleGenerateParameterTypes();
      this.handleCheckValidation();
    });
    this.onChange(fieldsValue.Statement, null);
  }

  generateSaveScript(item) {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    fieldsValue.Statement = this.getActionScript(item, "update");
    if (!this.props.actionToEdit || !this.props.actionToEdit?.name) {
      fieldsValue.ActionName = `${item.title}Save`;
    }
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.setState({ code: fieldsValue.Statement }, () => {
      this.checkAndHandleGenerateParameterTypes();
    });
    this.onChange(fieldsValue.Statement, null);
  }

  generateDeleteScript(item) {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    fieldsValue.Statement = this.getActionScript(item, "delete");
    if (!this.props.actionToEdit || !this.props.actionToEdit?.name) {
      fieldsValue.ActionName = `${item.title}Delete`;
    }
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.setState({ code: fieldsValue.Statement }, () => {
      this.checkAndHandleGenerateParameterTypes();
    });
    this.onChange(fieldsValue.Statement, null);
  }

  generateSelectByIdScript(item) {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    fieldsValue.Statement = this.getActionScript(item, "selectById");
    if (!this.props.actionToEdit || !this.props.actionToEdit?.name) {
      fieldsValue.ActionName = `${item.title}ById`;
    }
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.setState({ code: fieldsValue.Statement }, () => {
      this.checkAndHandleGenerateParameterTypes();
    });
    this.onChange(fieldsValue.Statement, null);
  }

  generateSearchByKeywordScript(item) {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    fieldsValue.Statement = this.getActionScript(item, "searchByKeyword");
    if (!this.props.actionToEdit || !this.props.actionToEdit?.name) {
      fieldsValue.ActionName = `${item.title}Search`;
    }
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.setState({ code: fieldsValue.Statement }, () => {
      this.checkAndHandleGenerateParameterTypes();
    });
    this.onChange(fieldsValue.Statement, null);
  }

  generateSearchWithPaginationScript(item) {
    const fieldsValue = this.formRef.current?.getFieldsValue();
    fieldsValue.Statement = this.getActionScript(item, "searchWithPagination");
    if (!this.props.actionToEdit || !this.props.actionToEdit?.name) {
      fieldsValue.ActionName = `${item.title}SearchWithPagination`;
    }
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.setState({ code: fieldsValue.Statement }, () => {
      this.checkAndHandleGenerateParameterTypes();
    });
    this.onChange(fieldsValue.Statement, null);
  }

  itemDblClicked = (item) => {
    if (item.isLeaf === true) return;
    this.generateSelectScript(item);
  };

  isUnknownParameterExists = (parameters: IActionParameter[]) => {
    const isUnknownExist = parameters.find((x) => x.Typename == "Unknown");

    if (isUnknownExist) {
      return false;
    }

    return true;
  };

  getDataSourceName = (datasource: IDatasourceDto) => {
    if (datasource.datasourceType == EDatasourceType.ManagedDb) {
      return "Managed DB (SQL Server)";
    }

    if (datasource.datasourceType == EDatasourceType.ManagedDbOracle) {
      return "Managed DB (Oracle)";
    }

    if (datasource.datasourceType == EDatasourceType.ManagedDbPostgre) {
      return "Managed DB (Postgresql)";
    }

    return datasource.name ?? "SCHEMA";
  };

  isPostgreQueryUseParams = (action: IActionDefinitionDto) => {
    return (
      (this.props?.datasource?.datasourceType === EDatasourceType.Postgre ||
        this.props?.datasource?.datasourceType === EDatasourceType.ManagedDbPostgre) &&
      (action.actionParameters?.length || action.parameters?.length) &&
      /\bbegin\b.*\bend\b|\bif\b/i.test(action.statement)
    );
  };

  saveAction = async (): Promise<unknown> => {
    // The submit button is live again as soon as the ✓ replaces the spinner, so a second Enter
    // would re-fire the save behind the closing dialog.
    if (!this.saveStatus.canStart) return undefined;
    this.saveStatus.start();

    const action = {} as IActionDefinitionDto;
    const name = this.formRef.current?.getFieldValue("ActionName");
    action.name = name;
    action.kDatasource_Id = this.props.datasource.id;

    action.statement = this.formRef.current?.getFieldValue("Statement");
    action.parameters = this.state.actionParameters;

    if (this.isPostgreQueryUseParams(action)) {
      KMainFunctions.displayWarningNotification(
        "PL/pgSQL script is not supported with parameters! Please use functions instead"
      );
      this.saveStatus.reset();
      return;
    }

    if (!this.isUnknownParameterExists(action.parameters)) {
      KMainFunctions.displayWarningNotification("Please fill parameter types before save.");
      this.saveStatus.reset();
      return;
    }

    if (this.props.actionToEdit.id) {
      action.id = this.props.actionToEdit.id;
    }
    try {
      const res = await DatasourceService.SaveAction(action);
      if (!res || res.status !== 200) {
        // A resolved response that is not 200: the `finally` reset used to rescue this branch.
        this.saveStatus.fail();
        return undefined;
      }
      this.setState({ showUnsavedChangesModal: false });
      const resData = res.data;
      const addedAction = resData?.find((x) => x.name === action.name);
      const actionId = addedAction?.id;
      if (this.props.folderToAdd && actionId) {
        const folder = this.props.sd.getFolderById(this.props.folderToAdd, this.props.sd.project.actionFolders);
        if (folder && folder.parentFolderId) {
          this.props.sd.addActionToFolderInFolder(folder.parentFolderId, this.props.folderToAdd, actionId);
        } else {
          this.props.sd.addActionToFolder(this.props.folderToAdd, actionId);
        }
      }
      // MCP callback: notify the MCP handler that the action was saved successfully
      if ((window as any).__mcpSqlActionCallback) {
        (window as any).__mcpSqlActionCallback({
          success: true,
          message: `Created SQL action "${action.name}"`,
          actionId: actionId || null,
          name: action.name,
          parameterCount: (action.parameters || []).length
        });
      }
      // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
      this.saveStatus.succeed((): void => {
        // The list behind the dialog lights the row up; the button has already said "Saved".
        requestDatasourceReveal({ name: action.name ?? "", parentId: this.props.datasource?.id });
        this.props.closeModal(true);
      });
    } catch (error) {
      this.saveStatus.fail();
      // MCP callback: notify the MCP handler about the error
      if ((window as any).__mcpSqlActionCallback) {
        (window as any).__mcpSqlActionCallback({
          error: `Failed to save action: ${error instanceof Error ? error.message : String(error)}`
        });
      }
      return error;
    }
    return undefined;
  };

  getClassnameForActiveStatus = (a): string => {
    if (a.isActive) return "kuika_rotate90deg";
    return "kuika_rotate0deg";
  };

  handleTest = async () => {
    const action = {} as IActionDefinitionDto;
    action.kDatasource_Id = this.props.datasource.id;
    action.statement =
      this.state.highlightedText && this.state.highlightedText.trim().length > 0
        ? this.state.highlightedText
        : this.formRef.current?.getFieldValue("Statement");
    if (action.statement === undefined) {
      action.statement = "";
    }
    action.actionParameters = this.state.actionParameters;

    if (this.props.actionToEdit.id) {
      action.id = this.props.actionToEdit.id;
    }

    if (this.isPostgreQueryUseParams(action)) {
      KMainFunctions.displayWarningNotification(
        "PL/pgSQL script is not supported with parameters! Please use functions instead"
      );
      this.saveStatus.reset();
      return;
    }

    if (!this.isUnknownParameterExists(action.actionParameters)) {
      KMainFunctions.displayWarningNotification("Please fill parameter types before save.");
      return;
    }
    try {
      this.setState({ isTestButtonLoading: true, runError: null });
      const startedAt: number = Date.now();
      const res = await DatasourceService.ExecuteQuery(action, true);
      if (res.data) {
        // A run always shows its result, even if the drawer was folded away.
        this.setState({ lastRunDurationMs: Date.now() - startedAt, drawerTab: "result", isResultDrawerOpen: true });
        this.setState({ isResultListOpen: true, resultList: res.data.resultList ?? [] }, () => {
          this.testResultsRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        });
        this.createColumnsForActionResult(res.data.resultList ?? [], res.data);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      // A run that failed is exactly when the console is wanted, and what it has to say is
      // on the Messages tab. The toast says it once and goes; the tab keeps it.
      this.setState({
        runError: readRunErrorMessage(error),
        drawerTab: "messages",
        isResultDrawerOpen: true
      });
      return error;
    } finally {
      this.setState({ isTestButtonLoading: false });
    }
  };

  // Always written, even when empty: a run with no columns (an UPDATE) must not keep the last
  // run's heading on the grid.
  createColumnsForActionResult = (resultList: any, data: any) => {
    const columns = [] as any[];
    if (resultList && resultList.length > 0) {
      resultList.forEach((result) => {
        for (const [key, value] of Object.entries(result)) {
          if (
            columns.findIndex((item) => item.dataIndex === key) === -1 &&
            !key.toLocaleLowerCase().includes("_deleted")
          ) {
            columns.push({
              title: key,
              dataIndex: key,
              key,
              width: "100px",
              value
            });
          }
        }
      });
      this.setState({ resultListColumns: columns });
    } else if (data) {
      for (const [key, value] of Object.entries(data.fieldTemplates)) {
        if (
          columns.findIndex((item) => item.dataIndex === key) === -1 &&
          !key.toLocaleLowerCase().includes("_deleted")
        ) {
          columns.push({
            title: key,
            dataIndex: key,
            key,
            width: "100px",
            value
          });
        }
      }
      this.setState({ resultListColumns: columns });
    }
  };

  nameValidator = async (_rule, value) => {
    if (!value) throw new Error("Please enter an Action name.");
    if (!/^[A-Za-z][A-Za-z0-9_]+$/.test(value)) throw new Error("Action name is invalid.");
  };

  handleCheckValidation = () => {
    if (this.state.errorMessage) this.setState({ errorMessage: undefined });

    this.formRef.current
      ?.validateFields()
      .then((_result) => {})
      .catch((errors) => {
        this.onValidationFailed(errors);
      });
  };

  onValidationFailed(errors) {
    const errorFields = errors?.errorFields;

    if (errorFields && errorFields.length > 0) {
      this.setState({ errorMessage: errorFields[0].errors[0] });
    }
  }

  /**
   * The editor opens at screen size less a 40px margin, and stays there: it is the tool you
   * work in, so there was nothing for a grow/shrink control to do. Only the width is stated:
   * the shell takes the height the body leaves it after the notice and the usages row above
   * it, through the flex chain in `.kuika__sql-action-modal`. It used to be given `height:
   * 100%` instead, which is the body's whole height — the rows above it then pushed the same
   * amount off the bottom, and what went past the edge was the result grid's pager.
   */
  getContentDimensions = () => ({ modalWidth: `calc(100vw - ${MODAL_MARGIN * 2}px)` });

  handleEditorPuntoChange = (fontSize: number) => {
    this.editorRef?.updateOptions({ fontSize });
    this.setState({ selectedPunto: fontSize });
  };

  /** Copies the selection when there is one, otherwise the whole editor. */
  handleCopyEditorContent = async (): Promise<void> => {
    const editor = this.editorRef;
    const model = editor?.getModel();
    if (!editor || !model) return;

    const selection = editor.getSelection();
    const hasSelection: boolean = !!selection && !selection.isEmpty();
    const text: string = hasSelection && selection ? model.getValueInRange(selection) : model.getValue();
    // The shared write: the Clipboard API, then the textarea fallback when permission is refused.
    await copyTextToClipboard(text);
  };

  handleUndo = () => {
    this.editorRef?.trigger("toolbar", "undo", null);
  };

  handleRedo = () => {
    this.editorRef?.trigger("toolbar", "redo", null);
  };

  refreshProblems = () => {
    this.setState({ problems: readModelProblems(this.sqlMonaco, this.editorRef?.getModel()) });
  };

  /** The Kuika AI dialect, or null for datasources it cannot help with. */
  getCoachType = (): EAICodeGeneratorType | null => {
    const type = this.props.datasource?.datasourceType;
    if (type === EDatasourceType.CustomDb || type === EDatasourceType.ManagedDb) return EAICodeGeneratorType.SQL;
    if (type === EDatasourceType.Postgre || type === EDatasourceType.ManagedDbPostgre) {
      return EAICodeGeneratorType.Postgre;
    }
    if (type === EDatasourceType.Oracle || type === EDatasourceType.ManagedDbOracle) return EAICodeGeneratorType.Oracle;
    return null;
  };

  getRunMeta = (): string | undefined => {
    if (!this.state.isResultListOpen) return undefined;
    const rows: number = this.state.resultList.length;
    const rowLabel: string = `${rows} ${rows === 1 ? "row" : "rows"}`;
    if (this.state.lastRunDurationMs === null) return rowLabel;
    return `${rowLabel} · ${this.state.lastRunDurationMs} ms`;
  };

  renderResultTable = (): JSX.Element => {
    if (!this.state.isResultListOpen) {
      return <div className="kuika__code-editor__drawer__empty">Run the query to see what it returns.</div>;
    }

    return (
      <CodeEditorResultTable
        rows={this.state.resultList}
        columns={this.state.resultListColumns}
        drawerHeight={this.state.drawerHeight}
      />
    );
  };

  goToProblem = (problem: ICodeEditorProblem): void => {
    const editor = this.editorRef;
    if (!editor) return;

    editor.revealLineInCenterIfOutsideViewport(problem.lineNumber);
    editor.setPosition({ lineNumber: problem.lineNumber, column: problem.column });
    editor.focus();
  };

  renderProblems = (): JSX.Element => (
    <CodeEditorProblemList problems={this.state.problems} runError={this.state.runError} onSelect={this.goToProblem} />
  );

  handleGenerateParameters = async () => {
    this.setState({ isParametersLoading: true });
    const statement = this.formRef.current?.getFieldValue("Statement");
    const parameters = this.state.actionParameters.map((x) => {
      return { name: x.name, Typename: x.Typename };
    });

    try {
      const res: any = await DatasourceService.GenerateSqlParamTypes(this.props.datasource?.id, statement, parameters);
      if (res && res.data && res.data.isSuccess) {
        let params: any = [...this.state.actionParameters];
        const newParameters = res.data.parameters;
        params.forEach((parameter: any) => {
          if (parameter.Typename === "Unknown") {
            const newParameter = newParameters.find((x) => x.name === parameter.name);
            if (newParameter) {
              parameter.Typename = EValueType[newParameter.valueType].toString();
            }
          }
        });
        const notExistedParams = newParameters.filter((x) => !params.find((y) => y.name === x.name));
        notExistedParams.forEach((x: any) => {
          params.push({ name: x.name, Typename: EValueType[x.valueType].toString() });
        });

        this.setState({ actionParameters: params });
      }
    } catch (error) {
      return error;
    } finally {
      // Asked, whatever came back. A name still Unknown after this is one the lookup could not
      // settle — it blocks saving, so from here on it is flagged. A failed or refused lookup counts:
      // the type stays unresolved either way, and what must not flag them is the wait before asking.
      const checkedTypeNames: string[] = Array.from(
        new Set([...this.state.checkedTypeNames, ...parameters.map((x): string => x.name)])
      );
      this.setState({ isParametersLoading: false, checkedTypeNames });
    }
  };

  handleSchemaSearch = (_event: React.ChangeEvent<HTMLInputElement>, value: string | number): void => {
    this.setState({ schemaSearch: String(value ?? "") });
  };

  render() {
    const { modalWidth } = this.getContentDimensions();
    // The dimming overlay and the close-confirmation dialog ask the same question the button's
    // status answers; they lift while the ✓ is still up, which is when the dialog is leaving anyway.
    const isSaving: boolean = this.state.saveStatus === "loading";
    const datasouce = this.props.sd.schemas.get(this.props.datasource?.id ?? "");
    let tables: string[] = [];
    if (datasouce) {
      tables = datasouce.tables.map((table) => table.title);
    }
    const coachType: EAICodeGeneratorType | null = this.getCoachType();
    return (
      <>
        <div>
          <KModal
            className="kuika__sql-action-modal kuika_approvalDetailModal"
            showModal
            closeModal={this.handleCloseModal}
            width={modalWidth}
            centered
            hideHeader
            hideCloseIcon
            // The form below carries its own spinner over the editor.
            dontRenderSpinner
          >
            <Form
              style={{ height: "100%" }}
              ref={this.formRef}
              onFinish={this.saveAction}
              onValuesChange={this.handleCheckValidation}
              onFinishFailed={this.handleCheckValidation}
              initialValues={
                this.props?.actionToEdit.id || this.props?.actionToEdit.statement
                  ? { Statement: this.props.actionToEdit.statement, ActionName: this.props.actionToEdit.name }
                  : {
                      Statement: undefined,
                      ActionName: KMainFunctions.defaultUniqueListElementNameGenerator(
                        this.props.sd.actionDefinitions,
                        "name",
                        "Action"
                      )
                    }
              }
            >
              <KuikaSpinnerSmall spinning={isSaving}>
                <ActionUsedInProcessAlert
                  actionId={this.props.actionToEdit?.id}
                  workFlowList={this.props.sd.workFlowList}
                  projectId={this.props.sd.project?.id}
                />
                <ActionUsagesButton actionId={this.props.actionToEdit?.id} actionName={this.props.actionToEdit?.name} />

                <div className="kuika__code-editor__shell kuika__code-editor__shell--flush">
                  <CodeEditorHeader
                    title={this.getDataSourceName(this.props.datasource)}
                    // The dialog keeps its always-open name field: it is mounted for the whole
                    // life of the dialog, so the form's values stay the name's source of truth
                    // here, and the datasource it writes to sits beside it.
                    renderTitle={(): React.ReactNode => (
                      <span className="kuika__code-editor__header__title">
                        <div className="kuika__code-editor__header__name">
                          <FormItem name="ActionName" rules={[{ validator: this.nameValidator }]}>
                            <KuikaInput data-testid="actionNameInput" placeholder="Custom action name" />
                          </FormItem>
                        </div>
                        <span className="kuika__code-editor__header__origin">
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <ellipse cx="12" cy="6" rx="8" ry="3" />
                            <path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
                          </svg>
                          {this.getDataSourceName(this.props.datasource)}
                        </span>
                      </span>
                    )}
                    // A dialog has no list behind it to go back to, so leaving is the X on
                    // the right rather than the panel's back control.
                    closeVariant="close"
                    status={
                      this.state.errorMessage ? (
                        <span className="kuika__code-editor__chip kuika__code-editor__chip--warn">
                          {this.state.errorMessage}
                        </span>
                      ) : undefined
                    }
                    runLabel="Run"
                    isRunning={this.state.isTestButtonLoading}
                    onRun={this.handleTest}
                    runTestId="sqlActionTestButton"
                    saveLabel={!this.props.actionToEdit || !this.props.actionToEdit?.name ? "CREATE" : "UPDATE"}
                    saveStatus={this.state.saveStatus}
                    isSaveDisabled={!!this.state.errorMessage}
                    saveTestId="actionCreateUpdateButton"
                    onClose={this.handleCloseModal}
                    closeTestId="closeSqlActionModal"
                  />

                  <div className="kuika__code-editor__body">
                    <div
                      className="kuika__sql-action-modal__schemas-container"
                      style={{
                        width: this.state.schemaAreaWidth,
                        height: "100%",
                        flexShrink: 0,
                        position: "relative"
                      }}
                    >
                      <div className="kuika__sql-action-modal__schema-search-container">
                        <KuikaInput
                          borderless
                          placeholder="Search schemas..."
                          value={this.state.schemaSearch}
                          onChange={this.handleSchemaSearch}
                          leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                          rightIcon={
                            this.state.schemaSearch ? (
                              <KuikaSvg
                                name="x"
                                width={12}
                                height={12}
                                pointer
                                onClick={(): void => this.setState({ schemaSearch: "" })}
                              />
                            ) : undefined
                          }
                          data-testid="schemaSearchInput"
                        />
                      </div>
                      <KuikaCollapsePanel
                        // The base class is spelled out because the primitive spreads
                        // `restProps` after its own `className`, so a caller's class replaces
                        // it rather than joining it — `screens-window` does the same.
                        className="kuika-collapse-panel kuika__ds-collapse"
                        style={{ flex: 1, overflow: "auto", height: "calc(100% - 40px)" }}
                        animated
                        items={this.getSchemaItems()}
                      />
                    </div>

                    <CodeEditorSplitter
                      orientation="vertical"
                      panel="before"
                      size={this.state.schemaAreaWidth}
                      onSizeChange={(schemaAreaWidth) => this.setState({ schemaAreaWidth })}
                      minSize={180}
                      maxSize={420}
                    />

                    <div className="kuika__code-editor__main">
                      <CodeEditorToolbar
                        onUndo={this.handleUndo}
                        onRedo={this.handleRedo}
                        onCopy={this.handleCopyEditorContent}
                        onFormat={this.handleFormatSql}
                        formatCompleted={this.state.formatCompleted}
                        isWrapEnabled={this.state.wrapStatus === "on"}
                        onToggleWrap={() => this.handleWrap(this.state.wrapStatus === "on" ? "off" : "on")}
                        onPasteAtCursor={this.handlePasteToLastClickedRow}
                        fontSize={this.state.selectedPunto}
                        onFontSizeChange={this.handleEditorPuntoChange}
                        problems={this.state.problems}
                        runError={this.state.runError}
                        onShowProblems={(): void => this.setState({ drawerTab: "messages", isResultDrawerOpen: true })}
                        cursor={this.state.cursor}
                      />

                      {this.state.pendingAiEdit && (
                        <AiPendingEditBar
                          plan={this.state.pendingAiEdit}
                          subject="query"
                          onApply={this.handleApplyPendingAiEdit}
                          onDiscard={this.aiPendingEdit.discard}
                          onChangeMode={this.aiPendingEdit.setMode}
                        />
                      )}

                      <div
                        className={`kuika__code-editor__editor${this.state.isDraggingTable ? " kuika__sql-action-modal__editor-drop-target--active" : ""}`}
                        onDragOver={this.handleEditorDragOver}
                        onDragLeave={this.handleEditorDragLeave}
                        onDrop={this.handleEditorDrop}
                      >
                        {this.state.dropMenu && (
                          <>
                            <div
                              className="kuika__sql-action-modal__drop-menu-backdrop"
                              onClick={(): void => this.setState({ dropMenu: null })}
                            />
                            <div
                              className="kuika__sql-action-modal__drop-menu"
                              style={{ left: this.state.dropMenu.x, top: this.state.dropMenu.y }}
                              tabIndex={-1}
                              ref={this.dropMenuRef}
                              onKeyDown={this.handleDropMenuKeyDown}
                              data-testid="sqlActionDropMenu"
                            >
                              {this.getTableScriptOptions(this.state.dropMenu.item).map(
                                (option: ISqlActionTableScriptOption, index: number) => (
                                  <button
                                    key={option.key}
                                    type="button"
                                    className={`kuika__sql-action-modal__drop-menu__item${index === this.state.dropMenuActiveIndex ? " kuika__sql-action-modal__drop-menu__item--active" : ""}`}
                                    onMouseEnter={(): void => this.setState({ dropMenuActiveIndex: index })}
                                    onClick={(): void => this.handleDropMenuSelect(option)}
                                  >
                                    {option.label}
                                  </button>
                                )
                              )}
                            </div>
                          </>
                        )}
                        <FormItem
                          name="Statement"
                          rules={[{ required: true, message: "Please enter a query" }]}
                          noStyle
                        >
                          <Editor
                            language="sql"
                            theme={CODE_EDITOR_THEME}
                            options={{
                              selectOnLineNumbers: true,
                              wordWrap: "on",
                              automaticLayout: true,
                              // The wrapper owns a drop: Monaco would otherwise paste the
                              // table's name as text before the menu could offer a query.
                              dropIntoEditor: { enabled: false },
                              fontSize: this.state.selectedPunto,
                              scrollbar: { alwaysConsumeMouseWheel: false }
                            }}
                            onChange={this.onChange}
                            onMount={this.handleEditorMount}
                            beforeMount={this.editorBeforeMount}
                          />
                        </FormItem>
                      </div>

                      {this.state.isResultDrawerOpen && (
                        <CodeEditorSplitter
                          orientation="horizontal"
                          size={this.state.drawerHeight}
                          onSizeChange={(drawerHeight) => this.setState({ drawerHeight })}
                          minSize={120}
                          maxSize={640}
                        />
                      )}

                      <CodeEditorDrawer
                        height={this.state.drawerHeight}
                        isCollapsed={!this.state.isResultDrawerOpen}
                        onToggleCollapsed={(isCollapsed: boolean): void =>
                          this.setState({ isResultDrawerOpen: !isCollapsed })
                        }
                        activeTab={this.state.drawerTab}
                        onTabChange={(drawerTab) => this.setState({ drawerTab })}
                        resultCount={this.state.isResultListOpen ? this.state.resultList.length : null}
                        messageCount={this.state.problems.length + (this.state.runError ? 1 : 0)}
                        meta={this.getRunMeta()}
                        resultContent={this.renderResultTable()}
                        messagesContent={this.renderProblems()}
                      />
                    </div>

                    <CodeEditorSplitter
                      orientation="vertical"
                      size={this.state.parametersPanelWidth}
                      onSizeChange={(parametersPanelWidth) => this.setState({ parametersPanelWidth })}
                      minSize={240}
                      maxSize={520}
                      isCollapsed={!this.state.isSidePanelOpened}
                      onToggleCollapsed={(isCollapsed) => this.setState({ isSidePanelOpened: !isCollapsed })}
                    />

                    {this.state.isSidePanelOpened && (
                      <CodeEditorSidePanel
                        width={this.state.parametersPanelWidth}
                        activeTab={this.state.sideTab}
                        onTabChange={(sideTab) => this.setState({ sideTab })}
                        parameterCount={this.state.actionParameters.length}
                        coachContent={
                          coachType === null ? null : (
                            <AICodeGenerator
                              type={coachType}
                              tables={tables}
                              datasourceId={this.props.datasource?.id}
                              suggestions={SQL_ACTION_AI_SUGGESTIONS}
                              onApplyCode={this.aiPendingEdit.request}
                            />
                          )
                        }
                        // The parameters come back from the server: previewed while there are none to show,
                        // kept on screen while a later edit re-reads their types.
                        parametersContent={
                          resolveLoadingPresentation(
                            this.state.isParametersLoading,
                            this.state.actionParameters.length > 0
                          ) === "skeleton" ? (
                            <KuikaSkeletonRegion label="Loading parameters">
                              <KuikaSkeletonList rows={3} density="compact" />
                            </KuikaSkeletonRegion>
                          ) : (
                            <SQLActionParameters
                              actionParameters={this.state.actionParameters}
                              updateParameters={(actionParameters) => {
                                this.setState({ actionParameters });
                              }}
                              searchable
                              showTypeIcons
                              searchableTypes
                              markUnknownTypes
                              checkedTypeNames={this.state.checkedTypeNames}
                              emptyStateName="parameter"
                              emptyStateIcon="adjustments-horizontal"
                              emptyStateDescription="Add a parameter to your query with @name to see it here."
                            />
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              </KuikaSpinnerSmall>
            </Form>
            <div className="kuika__sql-action-modal__drag-ghost" ref={this.dragGhostRef} aria-hidden="true">
              <KuikaBadge
                color="orange"
                leftIcon={<KuikaSvg name="table" width={14} height={14} />}
                content={<span ref={this.dragGhostLabelRef} />}
              />
              <span className="kuika__sql-action-modal__drag-ghost__badge">
                <KuikaSvg name="copy-plus" width={16} height={16} />
              </span>
            </div>
            <CloseConfirmationModal
              itemName={this.formRef.current?.getFieldValue("ActionName") || "Custom Action"}
              closeType={"Action"}
              triggerModal={this.state.showUnsavedChangesModal}
              isLoading={isSaving}
              updateAndCloseHandler={() => this.formRef.current?.submit()}
              closeWithoutSaveHandler={() => {
                this.props.closeModal();
              }}
              cancel={() => {
                this.setState({ showUnsavedChangesModal: false });
              }}
            />
          </KModal>
        </div>
      </>
    );
  }
}

const sqlActionModal = withHistory<ISqlActionModalProps>(SqlActionModal);
export { sqlActionModal as SqlActionModal };
